/**
 * What one boat-to-boat hit was. The physics is already spent by the time this
 * exists; this is the record the play log reads now and the damage pass reads
 * later, which is why it keeps where on each hull the blow landed.
 */
import { capsuleSpine, type HullCapsule } from './boatHull.ts';
import type { BoatHit } from '../host.ts';
import { clamp, dot, lengthSq, sub } from '../../math.ts';
import type { Vec2 } from '../../types.ts';

export type BoatContact = BoatHit;

/**
 * Position of a contact along a hull, 0 at the transom and 1 at the bow.
 * Clamped, so a blow on the rounded cap reads as the very end rather than
 * running off the scale.
 */
export function contactAlongHull(capsule: HullCapsule, point: Vec2): number {
  const spine = capsuleSpine(capsule);
  const axis = sub(spine.to, spine.from);
  const span = lengthSq(axis);

  if (span < 1e-9) {
    return 0.5;
  }

  return clamp(dot(sub(point, spine.from), axis) / span, 0, 1);
}

/**
 * Which seat drove into the other. A boat is closing when its own velocity
 * carries it toward the contact; when both do, the fault is shared and this is
 * null rather than arbitrarily picking one.
 */
export function faultFromClosing(
  fromId: number,
  intoId: number,
  fromVelocity: Vec2,
  intoVelocity: Vec2,
  normal: Vec2,
): number | null {
  const fromClosing = dot(fromVelocity, normal) > 0;
  const intoClosing = dot(intoVelocity, normal) < 0;

  if (fromClosing && intoClosing) {
    return null;
  }

  if (fromClosing) {
    return fromId;
  }

  return intoClosing ? intoId : null;
}

/** Was this a blow to the stern, where the rudder and shaft live? */
export function struckAstern(contact: BoatContact): boolean {
  return contact.intoAlong < 0.25;
}

/** Was it the bow that did the striking? */
export function struckWithBow(contact: BoatContact): boolean {
  return contact.fromAlong > 0.75;
}

/** Sort key so two contacts at the same instant always resolve in the same order. */
export function contactOrder(a: BoatContact, b: BoatContact): number {
  if (a.at !== b.at) {
    return a.at - b.at;
  }

  const lowA = Math.min(a.fromId, a.intoId);
  const lowB = Math.min(b.fromId, b.intoId);

  if (lowA !== lowB) {
    return lowA - lowB;
  }

  return Math.max(a.fromId, a.intoId) - Math.max(b.fromId, b.intoId);
}
